import Raven from 'raven-js';
import I18n from 'I18n';
import BaseDialog from 'customer-data-ui-utilities/dialog/BaseDialog';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { INSERT_HUBSPOT_VIDEO } from 'rich-text-lib/constants/usageTracking';
import { VideoPanel } from 'video-components-external/components/VideoPanel';
import { anyOf, requirePublicVideo, requireAnyoneWithLinkVideo } from 'video-components-external/utils/insertionPredicates';
import { buildLegacySharePageUrl, buildSharePageUrl } from 'video-data-lib/utils/url';
import { useGetOrCreateShareKeyMutation } from '../../__generated__/chirp/com/hubspot/video/rpc/HubSpotVideoRpc.hooks';
import { buildPrivateVideoPosterUrl, getDefaultHsSitesDomain } from '../prosemirror/commands/video/buildVideoSharePageUrl';
import { useFetchEmailTrackingDomain, useIsUngatedForHubSpotVideoCapture, useIsUngatedForSharePageV1 } from '../prosemirror/commands/video/videoHooks';
import { Tracker } from '../utils/usageTracker';
import { isPrivateFile } from './utils/Images';
import { AsyncCaptureModal } from './components/AsyncCaptureModal';
import GoVideoContainer from './GoVideoContainer';
import { jsx as _jsx } from "react/jsx-runtime";
export const VideoModalTypes = {
  INSERT_VIDEO_HUBSPOT: 'insert-video-hubspot',
  INSERT_VIDEO_VIDYARD: 'insert-video-vidyard',
  INSERT_VIDEO_HUBSPOT_CAPTURE: 'insert-video-hubspot-capture'
};
const GoVideoDialog = props => {
  const {
    modalType
  } = props;
  const isHubSpotVideoInsert = modalType === VideoModalTypes.INSERT_VIDEO_HUBSPOT || modalType === VideoModalTypes.INSERT_VIDEO_HUBSPOT_CAPTURE;
  const isUngatedForHubSpotVideoCapture = useIsUngatedForHubSpotVideoCapture();
  const isUngatedForSharePageV1 = useIsUngatedForSharePageV1();
  const {
    domain: emailTrackingDomain
  } = useFetchEmailTrackingDomain(!isHubSpotVideoInsert);
  const sharePageDomain = emailTrackingDomain || getDefaultHsSitesDomain();
  const [generateShareKey] = useGetOrCreateShareKeyMutation();
  const insertVideo = ({
    title,
    url,
    thumbnailSrc
  }) => {
    var _props$onChange;
    (_props$onChange = props.onChange) === null || _props$onChange === void 0 || _props$onChange.call(props, {
      url,
      thumbnailSrc,
      videoTitle: title
    });
    Tracker.track('prosemirrorVideo', {
      action: INSERT_HUBSPOT_VIDEO,
      method: 'toolbar',
      watchPageUrl: url,
      videoTitle: title,
      fileSource: 'hubspot'
    });
  };
  const handleSelect = video => {
    void (async () => {
      if (video.visibility === 'ANYONE_WITH_LINK') {
        try {
          const {
            data
          } = await generateShareKey({
            variables: {
              fileId: video.sourceFileId
            }
          });
          const shareKey = data === null || data === void 0 ? void 0 : data.result.shareKey;
          if (!shareKey) {
            throw new Error('No share key generated');
          }
          const url = buildSharePageUrl(sharePageDomain, shareKey);
          insertVideo({
            title: video.title,
            thumbnailSrc: buildPrivateVideoPosterUrl(shareKey),
            url
          });
        } catch (_unused) {
          Raven.captureMessage('Failed to generate key for private video', {
            extra: {
              fileId: video.sourceFileId
            }
          });
          FloatingAlertStore.addAlert({
            type: 'danger',
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspot-prosemirror.insertMenu.videoOptions.error.message"
            }),
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspot-prosemirror.insertMenu.videoOptions.error.title"
            })
          });
        }
      } else {
        const url = isUngatedForSharePageV1 ? buildSharePageUrl(sharePageDomain, video.videoId) : buildLegacySharePageUrl(sharePageDomain, video.videoId);
        insertVideo({
          title: video.title,
          thumbnailSrc: video.posterUrl || video.thumbnailUrl,
          url
        });
      }
    })();
  };
  const handleRecordingUploaded = (file, video, shareKey) => {
    var _props$onChange2;
    const isPrivate = isPrivateFile(file);
    if (isPrivate && !shareKey) {
      Raven.captureMessage('Received private file with no shareKey from CaptureModal', {
        extra: {
          file,
          video
        }
      });
      FloatingAlertStore.addAlert({
        titleText: I18n.text('draftPlugins.capture.errorAlert.title'),
        message: I18n.text('draftPlugins.capture.errorAlert.message'),
        type: 'danger'
      });
      props.onClose();
      return;
    }
    const privateWithShareKey = isPrivate && shareKey;
    const thumbnailSrc = privateWithShareKey ? buildPrivateVideoPosterUrl(shareKey) : video.posterUrl || video.thumbnailUrl;
    const url = buildSharePageUrl(sharePageDomain, privateWithShareKey ? shareKey : video.videoId);
    (_props$onChange2 = props.onChange) === null || _props$onChange2 === void 0 || _props$onChange2.call(props, {
      url,
      thumbnailSrc,
      videoTitle: file.title
    });
    props.onClose();
    FloatingAlertStore.addAlert({
      titleText: I18n.text('draftPlugins.capture.successAlert.title'),
      message: I18n.text('draftPlugins.capture.successAlert.message'),
      type: 'success'
    });
    Tracker.track('prosemirrorVideo', {
      action: isPrivateFile(file) ? 'insert-capture-video-private' : 'insert-capture-video-public',
      method: 'toolbar',
      watchPageUrl: url,
      videoTitle: video.title,
      fileSource: 'hubspot-capture'
    });
  };
  if (modalType === VideoModalTypes.INSERT_VIDEO_HUBSPOT_CAPTURE) {
    return /*#__PURE__*/_jsx(AsyncCaptureModal, {
      showUploadVisibilityOptions: true,
      trackingScreen: "hubspot-prosemirror",
      onRecordingUploaded: handleRecordingUploaded,
      onClose: props.onClose
    });
  }
  if (isHubSpotVideoInsert) {
    return /*#__PURE__*/_jsx(VideoPanel, Object.assign({
      panelType: "PICKER",
      shouldEncodeToHlsOnInsert: false,
      pickerPanelOptions: {
        allowUpload: false
      },
      enablePrivateInsert: isUngatedForHubSpotVideoCapture
    }, isUngatedForHubSpotVideoCapture && {
      insertionCriteria: [anyOf([requirePublicVideo(), requireAnyoneWithLinkVideo()], I18n.text('hubspot-prosemirror.insertMenu.videoOptions.invalidVideo.visibility'))]
    }, {
      onSelect: handleSelect,
      onClose: () => props.onClose()
    }));
  }
  return /*#__PURE__*/_jsx(BaseDialog, {
    bodyClassName: "display-flex align-center justify-center",
    onReject: props.onClose,
    showCancelButton: false,
    showConfirmButton: false,
    title: I18n.text('draftPlugins.goVideoModal.title'),
    width: 625,
    size: "short",
    children: /*#__PURE__*/_jsx(GoVideoContainer, {
      isInServiceHub: props.isInServiceHub,
      onChange: props.onChange,
      onClose: props.onClose,
      onConfirm: props.onConfirm,
      trackingHandlers: props.trackingHandlers,
      recipientEmail: props.recipientEmail,
      isInlineVideo: props.isInlineVideo,
      showTitleCheckBox: props.showTitleCheckBox
    })
  });
};
export default GoVideoDialog;